import React, { useMemo, useState } from 'react';
import { Cloud, HardDrive, Youtube, Archive, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { MarketingContent } from '../../types/marketing';
import { WorkflowTemplate } from '../../utils/marketingTemplates';
import './MarketingTable.css';

interface MarketingTableProps {
  items: MarketingContent[];
  template: WorkflowTemplate;
  onSelect: (item: MarketingContent) => void;
}

type SortKey = 'title' | 'stage' | 'recordingDate' | 'publishDate' | 'updatedAt';
type SortDir = 'asc' | 'desc';

function fmtDate(d: Date | null | undefined): string {
  if (!d) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const MarketingTable: React.FC<MarketingTableProps> = ({ items, template, onSelect }) => {
  const [sortKey, setSortKey] = useState<SortKey>('updatedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const stageIndexMap = useMemo(() => {
    const m = new Map<string, number>();
    template.stages.forEach((s, i) => m.set(s.id, i));
    return m;
  }, [template]);

  const sorted = useMemo(() => {
    const factor = sortDir === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
      const av = sortValue(a, sortKey, stageIndexMap);
      const bv = sortValue(b, sortKey, stageIndexMap);
      if (av === bv) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return av < bv ? -factor : factor;
    });
  }, [items, sortKey, sortDir, stageIndexMap]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'title' ? 'asc' : 'desc');
    }
  };

  const sortIcon = (key: SortKey) => {
    if (sortKey !== key) return <ArrowUpDown size={11} className="mh-table-sort-icon" />;
    return sortDir === 'asc'
      ? <ArrowUp size={11} className="mh-table-sort-icon active" />
      : <ArrowDown size={11} className="mh-table-sort-icon active" />;
  };

  if (sorted.length === 0) {
    return (
      <div className="mh-table-empty">
        <p>No content yet</p>
        <span>Click "+ New Content" to start tracking your first podcast video.</span>
      </div>
    );
  }

  return (
    <div className="mh-table-wrap pm-scroll">
      <table className="mh-table">
        <thead>
          <tr>
            <th className="mh-table-th-sortable" onClick={() => toggleSort('title')}>
              Title {sortIcon('title')}
            </th>
            <th className="mh-table-th-sortable" onClick={() => toggleSort('stage')}>
              Current stage {sortIcon('stage')}
            </th>
            <th>Guest</th>
            <th className="mh-table-th-sortable" onClick={() => toggleSort('recordingDate')}>
              Recorded {sortIcon('recordingDate')}
            </th>
            <th className="mh-table-th-sortable" onClick={() => toggleSort('publishDate')}>
              Published {sortIcon('publishDate')}
            </th>
            <th>Links</th>
            <th className="mh-table-th-sortable" onClick={() => toggleSort('updatedAt')}>
              Updated {sortIcon('updatedAt')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(item => {
            const stage = template.stages.find(s => s.id === item.currentStageId) ?? template.stages[0];
            return (
              <tr key={item.id} onClick={() => onSelect(item)}>
                <td className="mh-table-title-cell">{item.title}</td>
                <td>
                  <span className="mh-table-stage-chip">
                    <span className="mh-table-stage-dot" style={{ background: stage.color }} />
                    {stage.label}
                  </span>
                </td>
                <td className="mh-table-muted">{item.guestName || '—'}</td>
                <td className="mh-table-muted">{fmtDate(item.recordingDate?.toDate?.())}</td>
                <td className="mh-table-muted">{fmtDate(item.publishDate?.toDate?.())}</td>
                <td>
                  <div className="mh-table-links">
                    {item.dropboxRawUrl && (
                      <a href={item.dropboxRawUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} title="Dropbox raw" aria-label="Dropbox raw">
                        <Cloud size={12} />
                      </a>
                    )}
                    {item.ssdPath && (
                      <span title={`SSD: ${item.ssdPath}`} aria-label="On SSD"><HardDrive size={12} /></span>
                    )}
                    {item.youtubeUrl && (
                      <a href={item.youtubeUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} title="YouTube" aria-label="YouTube">
                        <Youtube size={12} />
                      </a>
                    )}
                    {item.dropboxArchiveUrl && (
                      <a href={item.dropboxArchiveUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} title="Archive" aria-label="Archive">
                        <Archive size={12} />
                      </a>
                    )}
                    {!item.dropboxRawUrl && !item.ssdPath && !item.youtubeUrl && !item.dropboxArchiveUrl && (
                      <span className="mh-table-muted">—</span>
                    )}
                  </div>
                </td>
                <td className="mh-table-muted mh-table-mono">
                  {fmtDate(item.updatedAt?.toDate?.())}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

function sortValue(
  item: MarketingContent,
  key: SortKey,
  stageIndexMap: Map<string, number>
): string | number | null {
  switch (key) {
    case 'title':
      return item.title.toLowerCase();
    case 'stage':
      return stageIndexMap.get(item.currentStageId) ?? -1;
    case 'recordingDate':
      return item.recordingDate?.toMillis?.() ?? null;
    case 'publishDate':
      return item.publishDate?.toMillis?.() ?? null;
    case 'updatedAt':
      return item.updatedAt?.toMillis?.() ?? null;
    default:
      return null;
  }
}

export default MarketingTable;
